import Vue from "vue";

/**
 * Global prompt controller for step-up verification.
 *
 * Bridges a guarded action to the single global <StepUpDialog>:
 * - Callers use ensureVerified(method) (windowed) or requireVerification(method).
 * - StepUpDialog renders from `state` and calls settle(result) when finished.
 *
 * A successful verification marks the user "recently verified" for WINDOW_MS so a
 * burst of gated operations (e.g. inline score edits) doesn't re-prompt each time.
 *
 * One prompt at a time is sufficient — a user acts on one thing at a time.
 *
 * (C) 2026
 */

const WINDOW_MS = 10 * 60 * 1000; // 10 minutes

// Vue.observable makes `state` reactive so StepUpDialog re-renders on change.
const state = Vue.observable({ show: false, method: "" });
let resolver = null;
let verifiedUntil = 0;

const StepUpPrompt = {
  state,

  /**
   * Open the global step-up dialog.
   * @param {string} method 'totp' (default). 'passkey' reserved.
   * @returns {Promise<boolean>} resolves true if verified, false if cancelled/failed
   */
  requireVerification(method) {
    state.method = method || "totp";
    state.show = true;
    return new Promise((resolve) => {
      resolver = resolve;
    });
  },

  /**
   * Resolve immediately if still within the verified window; otherwise prompt.
   * @param {string} method
   * @returns {Promise<boolean>}
   */
  ensureVerified(method) {
    if (this.isRecentlyVerified()) return Promise.resolve(true);
    return this.requireVerification(method);
  },

  isRecentlyVerified() {
    return Date.now() < verifiedUntil;
  },

  clearVerified() {
    verifiedUntil = 0;
  },

  /**
   * Close the dialog and resolve the in-flight promise. A truthy result opens the
   * verified window.
   * @param {boolean} result
   */
  settle(result) {
    state.show = false;
    if (result) verifiedUntil = Date.now() + WINDOW_MS;
    const r = resolver;
    resolver = null;
    if (r) r(!!result);
  },
};

export default StepUpPrompt;
